/**
 * TemplateEditorPanel — Dev-only toolbar for editing robot templates
 * within the standard WorkshopScene. Allows loading, editing, creating,
 * deleting and saving templates to disk (via Vite endpoint).
 */
import Phaser from 'phaser';
import { RobotTemplate, getTemplates, getMutableTemplates, saveTemplatesToDisk } from '@/config/RobotTemplates';
import { CANVAS_WIDTH, COMPONENT_SPACING } from '@/config/GameConfig';
import { TECH, createSmallTechButton, createTechButton, showTextInputDialog } from '@/ui/TechUI';
import { SoundManager } from '@/systems/SoundManager';

// ── Layout ──
const BAR_H = 36;
const BAR_Y = 4;
const DEPTH = 50;
const BAR_BG = 0x1a0a2a;

export interface TemplateEditorCallbacks {
  /** Load a template by index into the workshop grid. */
  loadTemplate: (index: number) => void;
  /** Snapshot the current grid + rules into a template-compatible structure. */
  snapshotForTemplate: () => { components: RobotTemplate['components']; rules: RobotTemplate['rules'] };
  /** Get current design name. */
  getDesignName: () => string;
}

export class TemplateEditorPanel {
  private scene: Phaser.Scene;
  private callbacks: TemplateEditorCallbacks;
  private container!: Phaser.GameObjects.Container;

  // State
  private editingIndex: number = -1; // -1 = none
  private statusText!: Phaser.GameObjects.Text;
  private templateLabel!: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, callbacks: TemplateEditorCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.create();
  }

  private create(): void {
    this.container = this.scene.add.container(0, 0).setDepth(DEPTH);

    // Background bar at the very top
    const bg = this.scene.add.rectangle(CANVAS_WIDTH / 2, BAR_Y + BAR_H / 2, CANVAS_WIDTH, BAR_H, BAR_BG, 0.95);
    this.container.add(bg);

    // Label: "TEMPLATE DEV"
    const titleText = this.scene.add.text(8, BAR_Y + 4, 'TEMPLATE DEV', {
      fontSize: '11px', fontFamily: 'monospace', color: '#cc88ff',
    });
    this.container.add(titleText);

    const gap = 6;
    const cy = BAR_Y + BAR_H / 2;

    // Helper: place a button, advance bx by its actual width + gap
    const buttons: Phaser.GameObjects.Container[] = [];
    const addBtn = (label: string, color: number, cb: () => void): Phaser.GameObjects.Container => {
      const btn = createSmallTechButton(this.scene, 0, cy, label, color, cb);
      btn.setDepth(DEPTH + 1);
      this.container.add(btn);
      buttons.push(btn);
      return btn;
    };

    // ── Select template (prev / label / next) ──
    const prevBtn = addBtn('\u25C0', 0x8888cc, () => this.cycleTemplate(-1));
    this.templateLabel = this.scene.add.text(0, BAR_Y + 5, '(none)', {
      fontSize: '10px', fontFamily: 'monospace', color: '#ffffff',
      fixedWidth: 110,
    });
    this.container.add(this.templateLabel);
    const nextBtn = addBtn('\u25B6', 0x8888cc, () => this.cycleTemplate(1));

    // ── Action buttons ──
    const loadBtn = addBtn('Load', 0x44aaff, () => {
      if (this.editingIndex >= 0) { this.callbacks.loadTemplate(this.editingIndex); SoundManager.play('clickConfirm'); }
    });
    const saveBtn = addBtn('Save', TECH.SUCCESS, () => this.saveCurrentTemplate());
    const newBtn = addBtn('New', 0x88cc44, () => this.createNewTemplate());
    const delBtn = addBtn('Del', TECH.DANGER, () => this.deleteCurrentTemplate());
    const renameBtn = addBtn('Rename', 0xcccc44, () => this.renameCurrentTemplate());
    const colorBtn = addBtn('Color', 0xcc88ff, () => this.editColor());
    const diskBtn = addBtn('Write Disk', 0xff8844, async () => {
      const ok = await saveTemplatesToDisk();
      this.showStatus(ok ? 'Saved to disk!' : 'Save failed', ok ? '#44ff88' : '#ff4444');
      SoundManager.play(ok ? 'clickConfirm' : 'clickCancel');
    });

    // Status text (positioned after layout)
    this.statusText = this.scene.add.text(0, BAR_Y + 6, '', {
      fontSize: '10px', fontFamily: 'monospace', color: '#44ff88',
    });
    this.container.add(this.statusText);

    // ── Layout: measure real widths and flow left-to-right ──
    let bx = 110; // after "TEMPLATE DEV" title
    // prev button
    const btnW = (btn: Phaser.GameObjects.Container) => (btn.getBounds().width || 40);
    prevBtn.setX(bx + btnW(prevBtn) / 2);
    bx += btnW(prevBtn) + 4;
    // template label
    this.templateLabel.setX(bx);
    bx += 114;
    // next button
    nextBtn.setX(bx + btnW(nextBtn) / 2);
    bx += btnW(nextBtn) + gap;
    // remaining buttons
    const remaining = [loadBtn, saveBtn, newBtn, delBtn, renameBtn, colorBtn, diskBtn];
    for (const btn of remaining) {
      const w = btnW(btn);
      btn.setX(bx + w / 2);
      bx += w + gap;
    }
    // status text after last button
    this.statusText.setX(bx + 4);

    // Start with first template selected
    if (getTemplates().length > 0) {
      this.editingIndex = 0;
      this.refreshLabel();
    }
  }

  // ── Template cycling ──

  private cycleTemplate(dir: number): void {
    const templates = getTemplates();
    if (templates.length === 0) return;
    this.editingIndex = (this.editingIndex + dir + templates.length) % templates.length;
    this.refreshLabel();
    SoundManager.play('click');
  }

  private refreshLabel(): void {
    const templates = getTemplates();
    if (this.editingIndex < 0 || this.editingIndex >= templates.length) {
      this.templateLabel.setText('(none)');
      return;
    }
    const tmpl = templates[this.editingIndex];
    this.templateLabel.setText(`${this.editingIndex + 1}/${templates.length} ${tmpl.name}`);
    const col = parseInt(tmpl.color.replace('#', ''), 16) || 0xffffff;
    this.templateLabel.setColor(tmpl.color);
  }

  // ── Save current grid as template ──

  private async saveCurrentTemplate(): Promise<void> {
    if (this.editingIndex < 0) {
      this.showStatus('No template selected', '#ff4444');
      return;
    }
    const snapshot = this.callbacks.snapshotForTemplate();
    if (snapshot.components.length === 0) {
      this.showStatus('Grid is empty', '#ff4444');
      return;
    }
    const templates = getMutableTemplates();
    const tmpl = templates[this.editingIndex];
    tmpl.components = snapshot.components;
    tmpl.rules = snapshot.rules.map(r => ({ ...r }));
    // Auto write to disk
    const ok = await saveTemplatesToDisk();
    this.showStatus(ok ? `Saved "${tmpl.name}" to disk` : `Saved "${tmpl.name}" (disk failed)`, ok ? '#44ff88' : '#ffaa44');
    SoundManager.play('clickConfirm');
  }

  // ── Create new template from current grid ──

  private createNewTemplate(): void {
    const snapshot = this.callbacks.snapshotForTemplate();
    if (snapshot.components.length === 0) {
      this.showStatus('Grid is empty', '#ff4444');
      return;
    }
    showTextInputDialog(this.scene, 'New Template Name', this.callbacks.getDesignName(), async (name) => {
      if (!name.trim()) return;
      const templates = getMutableTemplates();
      const newTmpl: RobotTemplate = {
        name: name.trim(),
        label: name.trim(),
        color: '#88aacc',
        bg: '#0a1a2a',
        components: snapshot.components,
        rules: snapshot.rules.map(r => ({ ...r })),
      };
      templates.push(newTmpl);
      this.editingIndex = templates.length - 1;
      this.refreshLabel();
      const ok = await saveTemplatesToDisk();
      this.showStatus(ok ? `Created "${name.trim()}"` : `Created "${name.trim()}" (disk failed)`, ok ? '#44ff88' : '#ffaa44');
      SoundManager.play('clickConfirm');
    });
  }

  // ── Delete current template ──

  private async deleteCurrentTemplate(): Promise<void> {
    if (this.editingIndex < 0) return;
    const templates = getMutableTemplates();
    const name = templates[this.editingIndex].name;
    templates.splice(this.editingIndex, 1);
    if (this.editingIndex >= templates.length) this.editingIndex = templates.length - 1;
    this.refreshLabel();
    const ok = await saveTemplatesToDisk();
    this.showStatus(ok ? `Deleted "${name}"` : `Deleted "${name}" (disk failed)`, ok ? '#ffaa44' : '#ff4444');
    SoundManager.play('clickCancel');
  }

  // ── Rename ──

  private renameCurrentTemplate(): void {
    if (this.editingIndex < 0) return;
    const templates = getMutableTemplates();
    const tmpl = templates[this.editingIndex];
    showTextInputDialog(this.scene, 'Rename Template', tmpl.name, async (name) => {
      if (!name.trim()) return;
      tmpl.name = name.trim();
      tmpl.label = name.trim();
      this.refreshLabel();
      const ok = await saveTemplatesToDisk();
      this.showStatus(ok ? `Renamed to "${name.trim()}"` : `Renamed (disk failed)`, ok ? '#44ff88' : '#ffaa44');
      SoundManager.play('click');
    });
  }

  // ── Color editor ──

  private editColor(): void {
    if (this.editingIndex < 0) return;
    const templates = getMutableTemplates();
    const tmpl = templates[this.editingIndex];
    showTextInputDialog(this.scene, 'Template Color (#hex)', tmpl.color, async (color) => {
      if (!color.trim().startsWith('#')) return;
      tmpl.color = color.trim();
      // Auto-generate dark bg from color
      const r = parseInt(color.slice(1, 3), 16) || 0;
      const g = parseInt(color.slice(3, 5), 16) || 0;
      const b = parseInt(color.slice(5, 7), 16) || 0;
      tmpl.bg = '#' + [r, g, b].map(c => Math.floor(c * 0.15).toString(16).padStart(2, '0')).join('');
      this.refreshLabel();
      const ok = await saveTemplatesToDisk();
      this.showStatus(ok ? `Color: ${tmpl.color}` : `Color set (disk failed)`, ok ? tmpl.color : '#ffaa44');
      SoundManager.play('click');
    }, { maxLength: 7, placeholder: '#rrggbb' });
  }

  // ── Status flash ──

  private showStatus(msg: string, color: string): void {
    this.statusText.setText(msg).setColor(color).setAlpha(1);
    this.scene.tweens.add({
      targets: this.statusText,
      alpha: 0,
      delay: 1500,
      duration: 500,
    });
  }

  /** Get the index of the currently selected template (-1 = none). */
  getEditingIndex(): number { return this.editingIndex; }

  destroy(): void {
    this.container.destroy();
  }
}
